/**
 * The agent-bridge state snapshot -- the sim-owned portion of the wire format
 * documented in the prototype's AGENTS.md. The shell merges in its own fields
 * (speed, replayMode, desynced) before pushing to the bridge; the wire contract
 * stays compatible with prototype clients.
 */
import { CHANCE_COSTS, NX, NY, TIERS, WAYPOINT_SET } from '../data/balance.js';
import type { Game } from './game.js';
import { waveDef } from './waves.js';
import type { GemTypeKey, SlateKey, SpecialKey } from '../data/balance.js';
import type { Phase } from './types.js';

export interface SnapshotCard {
  readonly i: number;
  readonly type: GemTypeKey | null;
  readonly tier: number | null;
  readonly tierName: string | null;
  readonly x: number;
  readonly y: number;
  readonly combine2: boolean;
  readonly combine4: boolean;
  readonly recipes: readonly SpecialKey[];
  /** Base slates forgeable anchored on this card (endless release, ADR 0008). */
  readonly slates: readonly SlateKey[];
}

export interface SnapshotSlate {
  readonly x: number;
  readonly y: number;
  readonly key: SlateKey;
}

export interface SnapshotGem {
  readonly x: number;
  readonly y: number;
  readonly type: GemTypeKey | null;
  readonly tier: number | null;
  readonly special: SpecialKey | null;
  readonly isNew: boolean;
}

export interface SimSnapshot {
  readonly phase: Phase;
  readonly gold: number;
  readonly lives: number;
  readonly wave: number;
  readonly chanceLevel: number;
  readonly tick: number;
  readonly stonesPlaced: number;
  readonly pendingRolls: number;
  readonly upgradeCost: number | null;
  readonly waveInfo: {
    readonly hp: number;
    readonly count: number;
    readonly armor: number;
    readonly tags: readonly string[];
  };
  readonly cards: readonly SnapshotCard[];
  readonly lifted: {
    readonly kind: 'rock' | 'gem';
    readonly type: GemTypeKey | null;
    readonly tier: number | null;
    readonly special: SpecialKey | null;
  } | null;
  readonly creepsAlive: number;
  readonly spawnQueue: number;
  readonly gems: readonly SnapshotGem[];
  readonly grid: readonly string[];
  /** Endless additions (additive; prototype-era clients ignore them). */
  readonly won: boolean;
  readonly canRetire: boolean;
  readonly slates: readonly SnapshotSlate[];
}

export function simSnapshot(game: Game): SimSnapshot {
  const gems: SnapshotGem[] = [];
  const slates: SnapshotSlate[] = [];
  for (let y = 0; y < NY; y++) {
    for (let x = 0; x < NX; x++) {
      const c = game.grid[y]![x];
      if (c && c.kind === 'gem') {
        gems.push({
          x,
          y,
          type: c.type ?? null,
          tier: c.tier ?? null,
          special: c.special ?? null,
          isNew: c.isNew,
        });
      } else if (c && c.kind === 'slate') {
        slates.push({ x, y, key: c.key });
      }
    }
  }
  const rows: string[] = [];
  for (let y = 0; y < NY; y++) {
    let s = '';
    for (let x = 0; x < NX; x++) {
      const c = game.grid[y]![x];
      s += WAYPOINT_SET.has(`${x},${y}`)
        ? 'W'
        : !c
          ? '.'
          : c.kind === 'rock'
            ? '#'
            : c.kind === 'slate'
              ? 'S'
              : 'G';
    }
    rows.push(s);
  }
  const def = waveDef(game.wave);
  return {
    phase: game.phase,
    gold: game.gold,
    lives: game.lives,
    wave: game.wave,
    chanceLevel: game.chanceLvl + 1,
    tick: game.tick,
    stonesPlaced: game.newGems.length,
    pendingRolls: game.pendingRolls.length,
    upgradeCost: game.chanceLvl < CHANCE_COSTS.length ? CHANCE_COSTS[game.chanceLvl]! : null,
    waveInfo: { hp: def.hp, count: def.count, armor: def.armor, tags: def.tags },
    cards: game.newGems.map((g, i) => ({
      i,
      type: g.type ?? null,
      tier: g.tier ?? null,
      tierName: g.tier !== undefined ? TIERS[g.tier]! : null,
      x: g.x,
      y: g.y,
      combine2:
        g.tier !== undefined && g.tier < 5 && game.identicalIn(game.combinePool(), g).length >= 2,
      combine4:
        g.tier !== undefined && g.tier < 4 && game.identicalIn(game.combinePool(), g).length >= 4,
      recipes: game.availableRecipes(g).map((r) => r.key),
      slates: game.availableSlates(g).map((r) => r.key),
    })),
    // Slates cannot be lifted, so the wire type stays 'rock' | 'gem'.
    lifted:
      game.lifted && game.lifted.kind !== 'slate'
        ? {
            kind: game.lifted.kind,
            type: game.lifted.kind === 'gem' ? (game.lifted.type ?? null) : null,
            tier: game.lifted.kind === 'gem' ? (game.lifted.tier ?? null) : null,
            special: game.lifted.kind === 'gem' ? (game.lifted.special ?? null) : null,
          }
        : null,
    creepsAlive: game.creeps.length,
    spawnQueue: game.spawnQueue,
    gems,
    grid: rows,
    won: game.won,
    canRetire: game.phase === 'build' && game.won && !game.recording.result,
    slates,
  };
}
